// Cliente del gateway `/gateway/sucursales/location` — la API que usa la
// SPA BuscadorSucursales para listar sucursales con todo el rich info
// (lat/lng, horario, telefono, CP, etc.).
// En Turnero la usamos como source-of-truth de la presentacion del chip y
// los pines del mapa; la disponibilidad de turnos sigue saliendo del
// backend turnero `/TurnoWeb/SucursalesAnexos/{localidadId}`.

import { sucursalesAxios } from '../config/sucursalesAxios';
import { toTitleCase } from '../constants/buscador';

type RawSucursal = {
  // Snake_case (DEV) o camelCase abreviado (QA) — ver adapter.
  cod_ubic?: number;
  codigoUbicacion?: number;
  suc_nombre?: string;
  sucursal?: string;
  desc_sucursal?: string;
  domicilio?: string;
  direccion?: string;
  cod_postal?: string;
  cp?: string;
  tel_directo?: string;
  telefono?: string;
  latitud?: string | number;
  longitud?: string | number;
  horario?: string;
  id_localidad?: number;
  desc_localidad?: string;
  id_partido?: number;
};

export interface BuscadorSucursalInfo {
  codUbic: number;
  nombre: string;
  domicilio: string;
  codPostal?: string;
  telefono?: string;
  latitud?: number;
  longitud?: number;
  horario?: string;
}

const toNum = (v: unknown): number =>
  typeof v === 'number' ? v : parseFloat(String(v ?? '0'));

// QA devuelve lat/lon como integer x1e6 (sin decimales). Normalizamos.
const normalizeCoord = (v: unknown): number | undefined => {
  if (v == null || v === '') return undefined;
  const n = toNum(v);
  if (Number.isNaN(n)) return undefined;
  return Math.abs(n) > 1000 ? n / 1_000_000 : n;
};

// Bounds AR — descarta coords corruptas que romperian el mapa.
const isValidArCoord = (lat?: number, lon?: number): boolean =>
  lat != null &&
  lon != null &&
  !Number.isNaN(lat) &&
  !Number.isNaN(lon) &&
  lat <= -21 &&
  lat >= -56 &&
  lon <= -53 &&
  lon >= -74;

const adapt = (r: RawSucursal): BuscadorSucursalInfo | null => {
  const codUbic = r.cod_ubic ?? r.codigoUbicacion;
  if (codUbic == null) return null;
  const lat = normalizeCoord(r.latitud);
  const lon = normalizeCoord(r.longitud);
  return {
    codUbic: Number(codUbic),
    nombre: (r.suc_nombre ?? r.sucursal ?? r.desc_sucursal ?? '').trim(),
    domicilio: (r.domicilio ?? r.direccion ?? '').trim(),
    codPostal: (r.cod_postal ?? r.cp)?.toString().trim() || undefined,
    telefono: (r.tel_directo ?? r.telefono)?.toString().trim() || undefined,
    latitud: isValidArCoord(lat, lon) ? lat : undefined,
    longitud: isValidArCoord(lat, lon) ? lon : undefined,
    horario: r.horario?.toString().trim() || undefined,
  };
};

// Devuelve `[]` en error/timeout/no-2xx para no romper la UI — la
// disponibilidad igual sale del backend turnero, asi que perder el rich
// info solo degrada el chip pero deja al usuario reservar.
export const fetchSucursalesInfoByLocalidad = async (
  idLocalidad: number | string
): Promise<BuscadorSucursalInfo[]> => {
  try {
    const res = await sucursalesAxios.get<RawSucursal[]>(`/branch/${idLocalidad}/0`);
    return (res.data ?? []).map(adapt).filter((x): x is BuscadorSucursalInfo => x !== null);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[buscadorSucursalesApi] fetch fail:', e);
    return [];
  }
};

type RawPartido = { id_partido?: number; desc_partido?: string };
type RawLocalidad = {
  id_localidad?: number;
  desc_localidad?: string;
  latitud?: string | number;
  longitud?: string | number;
};

interface PartidoGeo { idPartido: number; nombre: string }
interface LocalidadGeo {
  id: string;
  nombre: string;
  latitud?: number;
  longitud?: number;
}

export const fetchPartidosByZona = async (idProvincia: number): Promise<PartidoGeo[]> => {
  try {
    const res = await sucursalesAxios.get<RawPartido[]>(`/region/${idProvincia}`);
    return (res.data ?? [])
      .filter((p) => p.id_partido != null)
      .map((p) => ({ idPartido: p.id_partido!, nombre: toTitleCase((p.desc_partido ?? '').trim()) }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[buscadorSucursalesApi] fetchPartidosByZona fail:', e);
    return [];
  }
};

export const fetchLocalidadesByPartido = async (idPartido: number): Promise<LocalidadGeo[]> => {
  try {
    const res = await sucursalesAxios.get<RawLocalidad[]>(`/district/${idPartido}`);
    return (res.data ?? [])
      .filter((l) => l.id_localidad != null)
      .map((l) => {
        const lat = normalizeCoord(l.latitud);
        const lon = normalizeCoord(l.longitud);
        return {
          id: String(l.id_localidad!),
          nombre: (l.desc_localidad ?? '').trim(),
          latitud: isValidArCoord(lat, lon) ? lat : undefined,
          longitud: isValidArCoord(lat, lon) ? lon : undefined,
        };
      });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[buscadorSucursalesApi] fetchLocalidadesByPartido fail:', e);
    return [];
  }
};

// Lookup directo por codigo de ubicacion — backend devuelve object o array.
export const fetchSucursalInfoByCodigo = async (
  codUbic: number | string
): Promise<BuscadorSucursalInfo | null> => {
  if (codUbic == null || codUbic === '') return null;
  const url = `/branch/${codUbic}`;
  try {
    const res = await sucursalesAxios.get<RawSucursal | RawSucursal[]>(url);
    const first = Array.isArray(res.data) ? res.data[0] : res.data;
    if (!first) return null;
    const withCod: RawSucursal = first.cod_ubic == null && first.codigoUbicacion == null
      ? { ...first, codigoUbicacion: Number(codUbic) }
      : first;
    return adapt(withCod);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[buscadorSucursalesApi] fetch by codigo fail:', url, e);
    return null;
  }
};
